import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { AnswerWithComment, List } from 'src/api';
import { navigate } from '../../../../../core/utils';
import { useStore } from 'src/context';
import {
  useAgeFilteredList,
  useDependencyListAnswers,
  useDependencyListAnswersSecond,
  useDependencyListAnswersAll,
  useDependencyNotShow,
  useDependencyListVariantAnswersWoExt
} from '../../hooks';
import { QuestionProps } from '../props';

/**
 * <RadioQuestionRedesign /> props type
 */
type RadioQuestionRedesignProps = QuestionProps<AnswerWithComment> & {
  list: List[];
};

/**
 * <RadioQuestionRedesign /> props
 */
const useRadioQuestionRedesignProps = ({
  value,
  ...props
}: Partial<RadioQuestionRedesignProps>) => {
  const history = useHistory();

  const { list: filteredList } = useAgeFilteredList(props.list as List[]);
  const imageStyle = props.question?.image_style;

  const dependencyNotShowList = useDependencyNotShow(filteredList);
  //const list = useDependencyListAnswers(dependencyNotShowList);
  var list = props.question?.dependency_answers_list_variant
    ? useDependencyListVariantAnswersWoExt(props.question?.answers[0].list)
    : useDependencyListAnswers(dependencyNotShowList);

  list = props.question?.dependency_all
    ? useDependencyListAnswersAll(list, props.question?.dependency_all)
    : props.question?.dependency
    ? useDependencyListAnswers(list)
    : useDependencyListAnswersSecond(list);

  const {
    changeIsQuestionAnswered,
    changeQuestionValue,
    next,
    results,
    id,
    sendAnswers
  } = useStore();

  const checkDependency = () => {
    if (props.question?.dependency) {
      const { questionId } = props.question?.dependency;
      if (results[questionId] == '' && props.question.endQuestionary) {
        sendAnswers();
        navigate(`/summary/?id=${id}`, history.push);
      } else if (results[questionId] == '') {
        next();
      }
    }
  };

  list = props.question?.dependency_all
    ? useDependencyListAnswersAll(list, props.question?.dependency_all)
    : props.question?.dependency
    ? useDependencyListAnswers(list)
    : useDependencyListAnswersSecond(list);

  const isBigList = list.length >= 5;
  const skip = list.length === 0;
  const skipOneVariant = list.length === 1;

  const didAutoAdvanceRef = useRef(false);

  useEffect(() => {
    if (value) {
      changeIsQuestionAnswered(
        typeof value === 'string'
          ? !!value.length
          : !!value?.answer.length && !!value.comment.length
      );
    }
  }, [value]);

  useEffect(() => {
    checkDependency();
    if (skip) {
      next();
    }
  }, []);

  // First select the only option (so dependency checks see updated results)
  useEffect(() => {
    if (
      skipOneVariant &&
      !didAutoAdvanceRef.current &&
      !value &&
      list.length >= 1 &&
      props.question
    ) {
      const first = list[0];
      const autoValue = first.comment
        ? { answer: first.text, comment: '' }
        : first.text;
      changeQuestionValue(props.question, autoValue as any);
      changeIsQuestionAnswered(true);
    }
  }, [
    skipOneVariant,
    value,
    list,
    props.question,
    changeQuestionValue,
    changeIsQuestionAnswered
  ]);

  // Then advance once the value reflects the first option
  useEffect(() => {
    if (
      !skipOneVariant ||
      didAutoAdvanceRef.current ||
      list.length === 0 ||
      !props.question
    )
      return;

    const firstText = list[0]?.text;
    const selectedIsFirst =
      typeof value === 'string'
        ? value === firstText
        : (value as any)?.answer === firstText;

    if (selectedIsFirst) {
      didAutoAdvanceRef.current = true;
      const sourceItem = props.question?.answers?.[0]?.list?.find(
        (item: any) =>
          (item.value ? item.value.toString() : item.text) === firstText
      );
      next(sourceItem as any);
    }
  }, [skipOneVariant, value, list, props.question, next]);

  return {
    configuredList: list,
    isBigList,
    imageStyle
  };
};

export { useRadioQuestionRedesignProps };
